// services/kronium-embed/ui/src/utils/translations.ts
/**
 * Simple translations utility for Kronium embed chat.
 * Supports: cs, sk, hu, hr, en
 */

export type SupportedLanguage = 'cs' | 'sk' | 'hu' | 'hr' | 'en';

type TranslationKey =
  | 'tab.chat'
  | 'tab.you'
  | 'tab.files'
  | 'input.placeholder'
  | 'contact.title'
  | 'contact.name'
  | 'contact.nameOptional'
  | 'contact.email'
  | 'contact.phone'
  | 'contact.country'
  | 'contact.submit'
  | 'contact.continue'
  | 'contact.skip'
  | 'contact.privacyConsent'
  | 'contact.privacyLink'
  | 'contact.privacyError'
  | 'contact.success'
  | 'contact.update';

const translations: Record<SupportedLanguage, Record<TranslationKey, string>> = {
  cs: {
    'tab.chat': 'Chat',
    'tab.you': 'Vy',
    'tab.files': 'Soubory',
    'input.placeholder': 'Napsat zprávu...',
    'contact.title': 'Kontaktní údaje',
    'contact.name': 'Jméno',
    'contact.nameOptional': 'Jméno (nepovinné)',
    'contact.email': 'E-mail',
    'contact.phone': 'Telefon',
    'contact.country': 'Země',
    'contact.submit': 'Odeslat',
    'contact.continue': 'Pokračovat',
    'contact.skip': 'Přeskočit',
    'contact.privacyConsent': 'Souhlasím se',
    'contact.privacyLink': 'Zásadami ochrany osobních údajů',
    'contact.privacyError': 'Pro pokračování potvrďte souhlas se zpracováním osobních údajů',
    'contact.success': 'Děkujeme! Vaše údaje byly uloženy.',
    'contact.update': 'Aktualizovat údaje',
  },
  sk: {
    'tab.chat': 'Chat',
    'tab.you': 'Vy',
    'tab.files': 'Súbory',
    'input.placeholder': 'Napísať správu...',
    'contact.title': 'Kontaktné údaje',
    'contact.name': 'Meno',
    'contact.nameOptional': 'Meno (nepovinné)',
    'contact.email': 'E-mail',
    'contact.phone': 'Telefón',
    'contact.country': 'Krajina',
    'contact.submit': 'Odoslať',
    'contact.continue': 'Pokračovať',
    'contact.skip': 'Preskočiť',
    'contact.privacyConsent': 'Súhlasím so',
    'contact.privacyLink': 'Zásadami ochrany osobných údajov',
    'contact.privacyError': 'Pre pokračovanie potvrďte súhlas so spracovaním osobných údajov',
    'contact.success': 'Ďakujeme! Vaše údaje boli uložené.',
    'contact.update': 'Aktualizovať údaje',
  },
  hu: {
    'tab.chat': 'Chat',
    'tab.you': 'Ön',
    'tab.files': 'Fájlok',
    'input.placeholder': 'Üzenet írása...',
    'contact.title': 'Kapcsolattartási adatok',
    'contact.name': 'Név',
    'contact.nameOptional': 'Név (opcionális)',
    'contact.email': 'E-mail',
    'contact.phone': 'Telefon',
    'contact.country': 'Ország',
    'contact.submit': 'Küldés',
    'contact.continue': 'Folytatás',
    'contact.skip': 'Kihagyás',
    'contact.privacyConsent': 'Elfogadom az',
    'contact.privacyLink': 'Adatvédelmi irányelveket',
    'contact.privacyError': 'A folytatáshoz fogadja el az adatvédelmi irányelveket',
    'contact.success': 'Köszönjük! Adatait mentettük.',
    'contact.update': 'Adatok frissítése',
  },
  hr: {
    'tab.chat': 'Chat',
    'tab.you': 'Vi',
    'tab.files': 'Datoteke',
    'input.placeholder': 'Napišite poruku...',
    'contact.title': 'Kontakt podaci',
    'contact.name': 'Ime',
    'contact.nameOptional': 'Ime (neobavezno)',
    'contact.email': 'E-mail',
    'contact.phone': 'Telefon',
    'contact.country': 'Država',
    'contact.submit': 'Pošalji',
    'contact.continue': 'Nastavi',
    'contact.skip': 'Preskoči',
    'contact.privacyConsent': 'Prihvaćam',
    'contact.privacyLink': 'Politiku privatnosti',
    'contact.privacyError': 'Za nastavak prihvatite politiku privatnosti',
    'contact.success': 'Hvala! Vaši podaci su spremljeni.',
    'contact.update': 'Ažuriraj podatke',
  },
  en: {
    'tab.chat': 'Chat',
    'tab.you': 'You',
    'tab.files': 'Files',
    'input.placeholder': 'Message...',
    'contact.title': 'Contact Details',
    'contact.name': 'Name',
    'contact.nameOptional': 'Name (optional)',
    'contact.email': 'Email',
    'contact.phone': 'Phone',
    'contact.country': 'Country',
    'contact.submit': 'Submit',
    'contact.continue': 'Continue',
    'contact.skip': 'Skip',
    'contact.privacyConsent': 'I agree to the',
    'contact.privacyLink': 'Privacy Policy',
    'contact.privacyError': 'Please accept the privacy policy to continue',
    'contact.success': 'Thank you! Your details have been saved.',
    'contact.update': 'Update Details',
  },
};

/**
 * Get translation for a key in the specified language.
 * Falls back to English if translation not found.
 */
export function t(key: TranslationKey, language: string): string {
  const lang = (language as SupportedLanguage) || 'en';
  const langTranslations = translations[lang] || translations.en;
  return langTranslations[key] || translations.en[key] || key;
}

/**
 * Create a translator function bound to a specific language.
 */
export function createTranslator(language: string) {
  return (key: TranslationKey) => t(key, language);
}
